import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/react-hooks';
import styled, { ThemeProvider } from 'styled-components';
import get from 'lodash/get';
import { ThemeProvider as UIThemeProvider } from '@alefeducation/ui-elements-library-core';
import Layout from '../common/Layout/Layout';
import { Column, GridContainer, Row } from '../common/GridLayout/GridLayout';
import Tooltip from '../common/Tooltip/Tooltip';
import studentAssignment from '../../apollo/queries/studentAssignment.graphql';
import Spinner from '../common/Spinner/Spinner';
import StudentPopup from '../common/StudentPopup/StudentPopup';
import { getLangCode } from '../../utils/language';
import { useErrorHandler } from '../common/ErrorHandler/ErrorHandler';
import AssignmentContext from '../common/AssignmentContext/AssignmentContext';
import ErrorMessage from '../common/ErrorMessage/ErrorMessage';
import assignmentTypes from '../../utils/assignmentTypes';
import { getReturnUrlAndRestOfQueryParams } from '../../helpers/url';
import { PendoProvider } from '../PendoProvider/PendoProvider';
import SubmissionPanel from './SubmissionPanel';
import { AssignmentAttachmentContextProvider } from './AssignmentAttachmentContext';
import NewAssignmentPanel from './NewAssignmentPanel';

const AssignmentPanelColumnWrapper = styled(Column)`
  @media #{$medium-max} {
    height: unset;
  }
`;

const AssignmentSubmission = ({
  teacherAssignmentParams,
  alefAssignmentParams,
  assignmentId,
  studentId,
  isGradableItemsLockedByDefault,
  assignmentType,
  onGoBack,
  onSaveOrSubmit,
  displayHeader,
  shouldHideButtons,
  onButtonsStateChange,
  submissionHandler,
  closeHandler,
}) => {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir();
  const [isUpdatingCommentCompleted, setUpdatingCommentCompleted] = useState(
    false,
  );
  const [isSaveButtonClicked, setSaveButtonClicked] = useState(false);
  const [isSubmitButtonClicked, setIsSubmitButtonClicked] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);
  const [unsavedChangesPopupOpen, setUnsavedChangesPopupOpen] = useState(false);
  const [isAssignmentLocked, setIsAssignmentLocked] = useState(false);
  const [isLessonLocked, setIsLessonLocked] = useState(false);
  const [hasStudentLeftClass, setHasStudentLeftClass] = useState(false);
  const { handleError } = useErrorHandler();
  const handleErrorCallback = useCallback(() => {
    handleError();
  }, []);
  const { assignmentInstanceId, samePage } = teacherAssignmentParams;
  const { contentId, instructionalPlanId } = alefAssignmentParams;
  const { mloId, classId } =
    assignmentType === assignmentTypes.ALEF
      ? alefAssignmentParams
      : teacherAssignmentParams;

  const { data = {}, loading, refetch: refetchStudentAssignment } = useQuery(
    studentAssignment,
    {
      variables: {
        assignmentId,
        referrerId: assignmentInstanceId || mloId,
        mloId,
        classId,
        type: assignmentType,
        isGradableItemsLockedByDefault,
        contentId,
      },
      onError: handleErrorCallback,
      fetchPolicy: 'network-only',
      onCompleted: () => {
        if (data.studentAssignment) {
          setIsLessonLocked(data.studentAssignment.isLessonLocked);
          setHasStudentLeftClass(data.studentAssignment.hasStudentLeftClass);
          setIsAssignmentLocked(data.studentAssignment.isLocked);
        }
      },
    },
  );

  const saveSubmission = React.useCallback(
    () => setSaveButtonClicked(true),
    [],
  );
  const submitSubmission = React.useCallback(
    () => setIsSubmitButtonClicked(true),
    [],
  );

  const goBackToPreviousPage = React.useCallback(() => {
    setUnsavedChangesPopupOpen(false);
    onGoBack();
  }, []);

  const onGoBackHandler =
    !samePage &&
    React.useCallback(
      () => {
        if (isFormModified) setUnsavedChangesPopupOpen(true);
        else goBackToPreviousPage();
      },
      [isFormModified],
    );

  useEffect(
    () => {
      if (!displayHeader) {
        closeHandler({
          onClose: onGoBackHandler,
        });
      }
    },
    [displayHeader, isFormModified],
  );

  const closeUnsavedChangesPopup = React.useCallback(() =>
    setUnsavedChangesPopupOpen(false),
  );

  if (loading) {
    return <Spinner isCentered={true} testId="assignmentSpinner" />;
  }

  if (!data.studentAssignment) {
    handleErrorCallback();
    return <></>;
  }

  const assignmentData = data.studentAssignment.assignmentInstance;
  const submissionData = data.studentAssignment.submission;
  const isSubmitted =
    (submissionData &&
      !(
        submissionData.status === 'PENDING' ||
        submissionData.status === 'RE_REQUESTED'
      )) ||
    false;

  const { mloName, subjectCategory } = assignmentData;
  const langCode = getLangCode(assignmentData.language);
  const backToReturnUrl = termOrder =>
    function() {
      const returnUrl = getReturnUrlAndRestOfQueryParams();
      if (termOrder) {
        window.location.href = `${window.location.origin}${
          returnUrl.split('#')[0]
        }#/classes/${classId}/lessons/${termOrder}/all`;
      } else {
        window.location.href = `${window.location.origin}${returnUrl}`;
      }
    };

  const submissionDisabled =
    assignmentData &&
    !assignmentData.attachmentRequired &&
    !assignmentData.commentRequired;

  return (
    <AssignmentContext.Provider
      value={{
        assignmentParams: {
          assignmentId,
          assignmentInstanceId,
          classId,
          contentId,
          mloId,
          instructionalPlanId,
          studentId,
          isGradableItemsLockedByDefault,
        },
        assignmentData: { assignmentType },
        methods: { refetchStudentAssignment },
      }}
    >
      <ThemeProvider theme={{ dir, lang: assignmentData.language, langCode }}>
        <UIThemeProvider>
          <PendoProvider>
            {hasStudentLeftClass && (
              <ErrorMessage
                message={t(
                  'This assignment no longer exists. Please contact your teacher for further information',
                )}
                actionButtons={
                  <button
                    className="btn btn-submit"
                    onClick={backToReturnUrl(assignmentData.termNumber)}
                  >
                    {t('Okay')}
                  </button>
                }
              />
            )}

            {isLessonLocked && (
              <ErrorMessage
                message={t(
                  'This lesson has been locked by your teacher and you will no longer be able to complete it.',
                )}
                actionButtons={
                  <button
                    className="btn btn-submit"
                    onClick={backToReturnUrl(assignmentData.termNumber)}
                  >
                    {t('Okay')}
                  </button>
                }
              />
            )}

            {!isLessonLocked &&
              isAssignmentLocked && (
                <ErrorMessage
                  message={t(
                    'Your assignment has been locked by your teacher. Please return to the lesson.',
                  )}
                  actionButtons={
                    <button className="btn btn-submit" onClick={onGoBack}>
                      {t('Back to Lesson')}
                    </button>
                  }
                />
              )}
            {!isLessonLocked &&
              !isAssignmentLocked && (
                <Layout
                  onGoBack={onGoBackHandler}
                  backButtonText=""
                  title={mloName}
                  subjectCategory={subjectCategory}
                  icon="Close"
                  displayHeader={displayHeader}
                  isStudentJourney={true}
                >
                  <GridContainer>
                    <Row className="full-height">
                      <AssignmentAttachmentContextProvider>
                        <AssignmentPanelColumnWrapper
                          colSpan={`col-12 ${
                            submissionDisabled ? '' : 'col-lg-4'
                          }`}
                        >
                          <NewAssignmentPanel
                            isSubmitted={isSubmitted}
                            assignment={assignmentData}
                            isUpdatingCommentCompleted={
                              isUpdatingCommentCompleted
                            }
                            onSaveAction={saveSubmission}
                            onSubmitAction={submitSubmission}
                            submission={submissionData}
                            shouldHideButtons={shouldHideButtons}
                            onButtonsStateChange={onButtonsStateChange}
                          />
                        </AssignmentPanelColumnWrapper>
                        {submissionDisabled ? null : (
                          <Column colSpan="col-12 col-lg-8">
                            <SubmissionPanel
                              dueOn={
                                (assignmentData && assignmentData.dueOn) || ''
                              }
                              isSubmitted={isSubmitted}
                              attachmentRequired={
                                assignmentData &&
                                assignmentData.attachmentRequired
                              }
                              commentRequired={
                                assignmentData && assignmentData.commentRequired
                              }
                              setIsFormModified={setIsFormModified}
                              submission={submissionData}
                              setIsSubmitButtonClicked={
                                setIsSubmitButtonClicked
                              }
                              setUpdatingCommentComplete={
                                setUpdatingCommentCompleted
                              }
                              isSaveButtonClicked={isSaveButtonClicked}
                              isSubmitButtonClicked={isSubmitButtonClicked}
                              onSaveOrSubmit={onSaveOrSubmit}
                              submissionHandler={submissionHandler}
                              shouldHideButtons={shouldHideButtons}
                              onAssignmentLocked={() => {
                                setIsAssignmentLocked(true);
                                onButtonsStateChange({
                                  isSubmitButtonDisabled: true,
                                  isSaveButtonDisabled: true,
                                  // We only want to tell alef-fe that the button should be disabled
                                  // Extra states below is needed to preserve button visibilities
                                  // TODO extra states should not be here
                                  isSubmitted,
                                  requiresSubmission:
                                    get(assignmentData, 'commentRequired') ||
                                    get(assignmentData, 'attachmentRequired'),
                                });
                              }}
                              onLessonLocked={() => {
                                setIsLessonLocked(true);
                                onButtonsStateChange({
                                  isSubmitButtonDisabled: true,
                                  isSaveButtonDisabled: true,
                                  isSubmitted,
                                  requiresSubmission:
                                    get(assignmentData, 'commentRequired') ||
                                    get(assignmentData, 'attachmentRequired'),
                                });
                              }}
                            />
                          </Column>
                        )}
                      </AssignmentAttachmentContextProvider>
                    </Row>
                  </GridContainer>
                  <Tooltip />
                  {unsavedChangesPopupOpen && (
                    <StudentPopup
                      testId={'unsaved-changes-popup'}
                      onClickConfirm={goBackToPreviousPage}
                      onClickCancel={closeUnsavedChangesPopup}
                      buttonTextCancel={t('No')}
                      buttonTextConfirm={t('Yes')}
                      message={t(
                        'There are unsaved changes to the assignment. Do you want to discard the changes?',
                      )}
                      title={t('Unsaved Changes')}
                      isRTL={dir === 'rtl'}
                    />
                  )}
                </Layout>
              )}
          </PendoProvider>
        </UIThemeProvider>
      </ThemeProvider>
    </AssignmentContext.Provider>
  );
};

AssignmentSubmission.defaultProps = {
  teacherAssignmentParams: {},
  alefAssignmentParams: {},
  onGoBack: () => {},
  onSaveOrSubmit: () => {},
  displayHeader: true,
  shouldHideButtons: false,
  onButtonsStateChange: () => {},
  submissionHandler: () => {},
  closeHandler: () => {},
};

AssignmentSubmission.propTypes = {
  teacherAssignmentParams: PropTypes.shape({
    assignmentInstanceId: PropTypes.string.isRequired,
    mloId: PropTypes.string.isRequired,
    classId: PropTypes.string.isRequired,
    samePage: PropTypes.bool,
  }),
  alefAssignmentParams: PropTypes.shape({
    mloId: PropTypes.string.isRequired,
    classId: PropTypes.string.isRequired,
    contentId: PropTypes.string.isRequired,
    instructionalPlanId: PropTypes.string,
  }),
  assignmentType: PropTypes.string.isRequired,
  studentId: PropTypes.string.isRequired,
  isGradableItemsLockedByDefault: PropTypes.bool.isRequired,
  assignmentId: PropTypes.string.isRequired,
  onGoBack: PropTypes.func,
  onSaveOrSubmit: PropTypes.func,
  displayHeader: PropTypes.bool,
  shouldHideButtons: PropTypes.bool,
  onButtonsStateChange: PropTypes.func,
  submissionHandler: PropTypes.func,
  closeHandler: PropTypes.func,
};

export default AssignmentSubmission;
